import { GET, POST, PUT, PATCH, DELETE } from '@/shared/api/requests';
import {
  ChecklistTemplate,
  ChecklistSection,
  ChecklistItem,
  ConditionOption,
  ChecklistItemResponse,
  ActivityLogEntry,
} from '../types';
import { Checklist, WorkOrder } from '@features/work-orders/types';

// API Base URL - apiClient already includes /api/v1 prefix
const PUNCHLISTS_BASE_URL = '/punchlists';

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface GetChecklistsOptions {
  isTemplate?: boolean;
  search?: string;
  ordering?: string;
  page?: number;
  pageSize?: number;
}

const normalizeListResponse = <T>(data: T[] | PaginatedResponse<T>): PaginatedResponse<T> => {
  if (Array.isArray(data)) {
    return {
      count: data.length,
      next: null,
      previous: null,
      results: data,
    };
  }
  return data;
};

// Helper function to check if ID is a temporary ID
export const isTemporaryId = (id: string): boolean => {
  // Temporary IDs follow pattern: {timestamp}-{random}
  return /^\d+-[a-z0-9]+$/.test(id);
};

// Helper function to clean temporary IDs from template data
const cleanTemplateData = (template: Partial<ChecklistTemplate>): Partial<ChecklistTemplate> => {
  const cleaned: Partial<ChecklistTemplate> = {
    ...template,
  };

  // Clean sections
  if (cleaned.sections) {
    cleaned.sections = cleaned.sections.map((section) => {
      // Create a new object without the temporary ID
      const { id, ...sectionWithoutId } = section;
      const cleanedSection = isTemporaryId(id) ? sectionWithoutId : section;

      // Clean items
      if (section.items) {
        const cleanedItems = section.items.map((item) => {
          // Create a new object without the temporary ID
          const { id: itemId, section: itemSection, ...itemWithoutId } = item;
          let cleanedItem = isTemporaryId(itemId) ? itemWithoutId : item;

          // Clean condition options
          if (item.condition_options) {
            const cleanedOptions = item.condition_options.map((option) => {
              // Create a new object without the temporary ID
              const { id: optionId, item: optionItem, ...optionWithoutId } = option;
              let cleanedOption = isTemporaryId(optionId) ? optionWithoutId : option;

              // Remove temporary item reference
              if (option.item && isTemporaryId(option.item)) {
                cleanedOption = { ...cleanedOption, item: undefined } as any;
              }

              return cleanedOption as ConditionOption;
            });
            cleanedItem = { ...cleanedItem, condition_options: cleanedOptions };
          }

          // Remove temporary section reference
          if (item.section && isTemporaryId(item.section)) {
            cleanedItem = { ...cleanedItem, section: undefined } as any;
          }

          return cleanedItem as ChecklistItem;
        });
        cleanedSection.items = cleanedItems;
      }

      // Remove temporary checklist reference
      if (section.checklist && isTemporaryId(section.checklist)) {
        const { checklist, ...sectionWithoutChecklist } = cleanedSection;
        return sectionWithoutChecklist as ChecklistSection;
      }

      return cleanedSection as ChecklistSection;
    });
  }

  return cleaned;
};

// Checklist Template API Functions
export const getChecklists = async (
  projectId: string,
  options: GetChecklistsOptions = {}
): Promise<PaginatedResponse<ChecklistTemplate>> => {
  const { isTemplate, search, ordering, page, pageSize } = options;
  const params = new URLSearchParams({ project_id: projectId });
  if (isTemplate !== undefined) {
    params.append('is_template', isTemplate.toString());
  }
  if (search) {
    params.append('search', search);
  }
  if (ordering) {
    params.append('ordering', ordering);
  }
  if (page !== undefined) {
    params.append('page', String(page));
  }
  if (pageSize !== undefined) {
    params.append('page_size', String(pageSize));
  }

  const url = `${PUNCHLISTS_BASE_URL}/punchlists/?${params.toString()}`;

  const response = await GET<ChecklistTemplate[] | PaginatedResponse<ChecklistTemplate>>(url);
  return normalizeListResponse(response.data);
};

export const getChecklistById = async (id: string): Promise<ChecklistTemplate> => {
  const response = await GET<ChecklistTemplate>(`${PUNCHLISTS_BASE_URL}/punchlists/${id}/`);
  return response.data;
};

export const getChecklistWithDetails = async (
  id: string,
  projectId: string
): Promise<ChecklistTemplate> => {
  const params = new URLSearchParams({ project_id: projectId });
  const response = await GET<ChecklistTemplate>(
    `${PUNCHLISTS_BASE_URL}/punchlists/${id}/full/?${params.toString()}`
  );
  return response.data;
};

export const getPunchlistActivity = async (
  id: string,
  projectId: string
): Promise<ActivityLogEntry[]> => {
  const params = new URLSearchParams({ project_id: projectId });
  const response = await GET<ActivityLogEntry[]>(
    `${PUNCHLISTS_BASE_URL}/punchlists/${id}/activity/?${params.toString()}`
  );
  return response.data;
};

export const createChecklist = async (
  projectId: string,
  checklistData: Omit<
    ChecklistTemplate,
    'id' | 'created_by' | 'created_at' | 'updated_by' | 'updated_at' | 'project'
  >
): Promise<ChecklistTemplate> => {
  const params = new URLSearchParams({ project_id: projectId });
  const cleanedData = cleanTemplateData(checklistData);
  const response = await POST<typeof cleanedData>(
    `${PUNCHLISTS_BASE_URL}/punchlists/?${params.toString()}`,
    cleanedData
  );
  return response.data;
};

export const createChecklistBulk = async (
  projectId: string,
  checklistData: Omit<
    ChecklistTemplate,
    'id' | 'created_by' | 'created_at' | 'updated_by' | 'updated_at' | 'project'
  >
): Promise<ChecklistTemplate> => {
  const params = new URLSearchParams({ project_id: projectId });
  const cleanedData = cleanTemplateData(checklistData);
  const response = await POST<typeof cleanedData>(
    `${PUNCHLISTS_BASE_URL}/punchlists/bulk-create/?${params.toString()}`,
    cleanedData
  );
  return response.data;
};

export const updateChecklist = async (
  id: string,
  updates: Partial<ChecklistTemplate>,
  projectId: string
): Promise<ChecklistTemplate> => {
  const params = new URLSearchParams({ project_id: projectId });
  const cleanedUpdates = cleanTemplateData(updates);
  const response = await PATCH<typeof cleanedUpdates>(
    `${PUNCHLISTS_BASE_URL}/punchlists/${id}/?${params.toString()}`,
    cleanedUpdates
  );
  return response.data;
};

export const updateChecklistFull = async (
  id: string,
  checklistData: Partial<ChecklistTemplate>,
  projectId: string
): Promise<ChecklistTemplate> => {
  const params = new URLSearchParams({ project_id: projectId });
  const cleanedData = cleanTemplateData(checklistData);
  const response = await PUT<typeof cleanedData>(
    `${PUNCHLISTS_BASE_URL}/punchlists/${id}/full/?${params.toString()}`,
    cleanedData
  );
  return response.data;
};

export const updateChecklistFullPartial = async (
  id: string,
  updates: Partial<ChecklistTemplate>,
  projectId: string
): Promise<ChecklistTemplate> => {
  const params = new URLSearchParams({ project_id: projectId });
  const cleanedUpdates = cleanTemplateData(updates);
  const response = await PATCH<typeof cleanedUpdates>(
    `${PUNCHLISTS_BASE_URL}/punchlists/${id}/full/?${params.toString()}`,
    cleanedUpdates
  );
  return response.data;
};

export const deleteChecklist = async (id: string, projectId: string): Promise<void> => {
  const params = new URLSearchParams({ project_id: projectId });
  await DELETE(`${PUNCHLISTS_BASE_URL}/punchlists/${id}/?${params.toString()}`);
};

export const createChecklistFromTemplate = async (
  templateId: string,
  data: { title: string; area?: number | null },
  projectId: string
): Promise<ChecklistTemplate> => {
  const params = new URLSearchParams({ project_id: projectId });
  const response = await POST<{ title: string }>(
    `${PUNCHLISTS_BASE_URL}/punchlists/${templateId}/create-instance/?${params.toString()}`,
    data
  );
  return response.data;
};

// Section API Functions
export const createSection = async (
  sectionData: Omit<ChecklistSection, 'id'>
): Promise<ChecklistSection> => {
  const response = await POST<Omit<ChecklistSection, 'id'>>(
    `${PUNCHLISTS_BASE_URL}/sections/`,
    sectionData
  );
  return response.data;
};

export const updateSection = async (
  id: string,
  updates: Partial<ChecklistSection>
): Promise<ChecklistSection> => {
  const response = await PATCH<Partial<ChecklistSection>>(
    `${PUNCHLISTS_BASE_URL}/sections/${id}/`,
    updates
  );
  return response.data;
};

export const deleteSection = async (id: string, projectId: string): Promise<void> => {
  const params = new URLSearchParams({ project_id: projectId });
  await DELETE(`${PUNCHLISTS_BASE_URL}/sections/${id}/?${params.toString()}`);
};

// Item API Functions
export const createItem = async (
  itemData: Omit<ChecklistItem, 'id' | 'is_completed' | 'response'>
): Promise<ChecklistItem> => {
  const response = await POST<Omit<ChecklistItem, 'id' | 'is_completed' | 'response'>>(
    `${PUNCHLISTS_BASE_URL}/items/`,
    itemData
  );
  return response.data;
};

export const updateItem = async (
  id: string,
  updates: Partial<ChecklistItem>
): Promise<ChecklistItem> => {
  const response = await PATCH<Partial<ChecklistItem>>(
    `${PUNCHLISTS_BASE_URL}/items/${id}/`,
    updates
  );
  return response.data;
};

export const deleteItem = async (id: string, projectId: string): Promise<void> => {
  const params = new URLSearchParams({ project_id: projectId });
  await DELETE(`${PUNCHLISTS_BASE_URL}/items/${id}/?${params.toString()}`);
};

export const respondToItem = async (
  itemId: string,
  responseData: {
    boolean_value?: boolean;
    condition_value?: string; // UUID of condition option
    text_value?: string;
    photo_value?: File;
    signature_value?: Record<string, unknown>;
  }
): Promise<{ id: string; item: string; display_value: string; [key: string]: unknown }> => {
  const response = await POST<typeof responseData>(
    `${PUNCHLISTS_BASE_URL}/items/${itemId}/respond/`,
    responseData
  );
  return response.data;
};

// Condition Options API Functions
export const createConditionOption = async (
  optionData: Omit<ConditionOption, 'id'>
): Promise<ConditionOption> => {
  const response = await POST<Omit<ConditionOption, 'id'>>(
    `${PUNCHLISTS_BASE_URL}/condition-options/`,
    optionData
  );
  return response.data;
};

export const updateConditionOption = async (
  id: string,
  updates: Partial<ConditionOption>
): Promise<ConditionOption> => {
  const response = await PATCH<Partial<ConditionOption>>(
    `${PUNCHLISTS_BASE_URL}/condition-options/${id}/`,
    updates
  );
  return response.data;
};

export const deleteConditionOption = async (id: string): Promise<void> => {
  await DELETE(`${PUNCHLISTS_BASE_URL}/condition-options/${id}/`);
};

// Work Order Update Function
export const updateWorkOrderWithChecklist = async (
  workOrderId: string,
  checklistId: string,
  projectId: string
): Promise<WorkOrder> => {
  const params = new URLSearchParams({ project_id: projectId });
  const response = await PATCH(
    `/property-manager/work-orders/${workOrderId}/?${params.toString()}`,
    { checklist_id: checklistId }
  );
  return response.data as WorkOrder;
};

export const createInstanceForWorkOrder = async (
  templateId: string,
  title: string,
  workOrderId: string,
  projectId: string
): Promise<{ checklist: Checklist; workOrder: WorkOrder }> => {
  // Create checklist instance
  const checklistResponse = await POST(
    `${PUNCHLISTS_BASE_URL}/punchlists/${templateId}/create-instance/?project_id=${projectId}`,
    { title }
  );

  const checklist = checklistResponse.data;

  // Update work order with checklist
  const workOrderResponse = await updateWorkOrderWithChecklist(
    workOrderId,
    checklist.id,
    projectId
  );

  return { checklist, workOrder: workOrderResponse };
};

// Checklist Item Response API Function
export const respondToChecklistItem = async (
  itemId: string,
  response: {
    boolean_value?: boolean;
    condition_value?: string;
    text_value?: string;
    photo_value?: string;
    signature_value?: Record<string, unknown>;
  },
  projectId: string
): Promise<ChecklistItemResponse> => {
  const params = new URLSearchParams({ project_id: projectId });
  const apiResponse = await POST(
    `${PUNCHLISTS_BASE_URL}/items/${itemId}/respond/?${params.toString()}`,
    response
  );
  return apiResponse.data;
};

// Convenience function for Yes/No items
export const toggleChecklistItemCompletion = async (
  itemId: string,
  isCompleted: boolean,
  projectId: string
): Promise<ChecklistItemResponse> => {
  return respondToChecklistItem(itemId, { boolean_value: isCompleted }, projectId);
};

// Convenience function for Free Text items
export const updateChecklistItemText = async (
  itemId: string,
  textValue: string,
  projectId: string
): Promise<ChecklistItemResponse> => {
  return respondToChecklistItem(itemId, { text_value: textValue }, projectId);
};

// Convenience function for Condition items
export const updateChecklistItemCondition = async (
  itemId: string,
  conditionValue: string,
  projectId: string
): Promise<ChecklistItemResponse> => {
  return respondToChecklistItem(itemId, { condition_value: conditionValue }, projectId);
};
